import { Box, useMediaQuery } from "@mui/material";
import { color } from "chart.js/helpers";
import { defaults } from "chart.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { useRef, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import { useTheme } from "@emotion/react";
import ZoomControls from "./ZoomControls";
import { formatNumber, formatNumberWithLocale , formatAxisValue} from "../../utils/charts";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ChartDataLabels,
    zoomPlugin
);


export default function MultipleAxis({ data, showDataLabels, hideSubregions, title, regions }) {
    const theme = useTheme();
    const ref = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);

    // Combine datasets: always show the original datasets (bars), and add compareDatasets (lines) when hideSubregions is false
    let combinedDatasets = [...(data.datasets || [])];

    // Create marker datasets for excluded values and missing data
    const markerDatasets = [];
    
    // if (data.excluded && Array.isArray(data.excluded)) {
    //     // Excluded values markers (triangles) - use the actual y value from excluded data
    //     const excludedPoints = data.labels.map((label, index) => {
    //         const excludedItem = data.excluded[index];
    //         return excludedItem ? { x: label, y: excludedItem.y, label: excludedItem.label } : null;
    //     }).filter(point => point !== null);

    //     if (excludedPoints.length > 0) {
    //         // Determine which y-axis to use based on the first dataset that has percentage unit
    //         const hasPercentageData = data.datasets.some(ds => ds.unit === '%');
    //         const yAxisID = hasPercentageData ? 'y-axis-2' : 'y-axis-1';
            
    //         markerDatasets.push({
    //             label: 'Excluded values',
    //             type: 'scatter',
    //             data: excludedPoints,
    //             yAxisID: yAxisID,
    //             pointStyle: 'triangle',
    //             pointRadius: 6,
    //             pointHoverRadius: 8,
    //             backgroundColor: '#ff9800',
    //             borderColor: '#ff9800',
    //             order: 0,
    //             showLine: false,
    //         });
    //     }

    //     // Missing data markers (crosses)
    //     const missingPoints = data.labels.map((label, index) => {
    //         // Check if this label has no data in any dataset and is not excluded
    //         const hasData = data.datasets.some(dataset => 
    //             dataset.data.some(point => point.x === label && point.y !== null && point.y !== undefined)
    //         );
    //         const isExcluded = data.excluded[index] !== null;
            
    //         return (!hasData && !isExcluded) ? { x: label, y: 1, label: 'No data available' } : null;
    //     }).filter(point => point !== null);

    //     if (missingPoints.length > 0) {
    //         markerDatasets.push({
    //             label: 'No data',
    //             type: 'scatter',
    //             data: missingPoints,
    //             yAxisID: 'marker-axis',
    //             pointStyle: 'cross',
    //             pointRadius: 6,
    //             pointHoverRadius: 8,
    //             backgroundColor: '#9e9e9e',
    //             borderColor: '#9e9e9e',
    //             order: 0,
    //             showLine: false,
    //         });
    //     }
    // }

    if (hideSubregions && data.compareDatasets) {
        // Add the regional comparison datasets, preserving their original type (bar/line based on unit)
        const regionalDatasets = data.compareDatasets.map((dataset, index) => ({
            ...dataset,
            // Remove stacking for regional datasets if useSideBySideBars is true, otherwise keep the stack value
            stack: data.useSideBySideBars ? undefined : dataset.stack,
            // Preserve the original type from transform function, but add line-specific properties if it's a line
            ...(dataset.type === 'line' ? {
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5,
            } : {}),
            order: dataset.type === 'line' ? 1 : 2, // Lines in front of bars
        }));

        combinedDatasets = [...combinedDatasets, ...regionalDatasets];
    }

    // Deduplicate by label + axis so we don't render two identical series on the same axis
    const seenSeries = new Set();
    combinedDatasets = combinedDatasets.filter(ds => {
        const normalizedLabel = (ds.label || '').toString().trim().toLowerCase();
        const key = `${normalizedLabel}__${ds.yAxisID || ''}`;
        if (seenSeries.has(key)) return false;
        seenSeries.add(key);
        return true;
    });

    // filter out from combinedDatasets any item that has the item.y equal to "NaN"
    combinedDatasets.forEach((dataset) => {
        if (dataset.data) {
            dataset.data = dataset.data.filter((item) => !Number.isNaN(item.y));
        }
    });

    // If the same label appears across different axes (e.g., % vs absolute),
    // disambiguate legend text by appending the unit.
    const disambiguateLabels = (arr) => {
        const counts = arr.reduce((acc, ds) => {
            const norm = (ds.label || '').toString().trim().toLowerCase();
            acc[norm] = (acc[norm] || 0) + 1;
            return acc;
        }, {});

        return arr.map(ds => {
            const norm = (ds.label || '').toString().trim().toLowerCase();
            if (counts[norm] > 1) {
                const unitSuffix = ds.unit ? ` (${ds.unit})` : '';
                return { ...ds, label: `${ds.label}${unitSuffix}` };
            }
            return ds;
        });
    };

    combinedDatasets = disambiguateLabels(combinedDatasets);

    // Add marker datasets to combined datasets
    combinedDatasets = [...combinedDatasets, ...markerDatasets];

    const hasYAxis1 = combinedDatasets.some(ds => ds.yAxisID === 'y-axis-1');
    const hasYAxis2 = combinedDatasets.some(ds => ds.yAxisID === 'y-axis-2');

    const options = {
        layout: {
            padding: {
                top: isMobile ? 10 : 18,
                right: isMobile ? 8 : 15,
                bottom: isMobile ? 10 : 12,
                left: isMobile ? 5 : 15,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                type: "category",
                labels: !hideSubregions ? data.labels : data.compareLabels,
                grid: {
                    display: false,
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                },
            },
            ...(hasYAxis2 && {
                "y-axis-2": {
                    type: "linear",
                    position: "right",
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: combinedDatasets.find(ds => ds.type !== 'scatter' && ds.yAxisID === 'y-axis-2')?.unit || "",
                        color: theme.palette.primary.fontColor,
                        padding: 0
                    },
                    grid: {
                        color: theme.palette.mode === "dark" ? "#333" : "#e7e7e7",
                        display: false,
                    },
                    ticks: {
                        color: theme.palette.primary.fontColor,
                        padding: 10,
                        // Zooming or panning leaves the axis ending wherever the drag stopped, and Chart.js labels
                        // that end too - 23.7 above 20, 48.3 above 45. Only the regular steps are labelled.
                        includeBounds: false,
                        callback: function (value, index, ticks) {
                            // Shared with the other charts: decimals come from the tick step, so a series
                            // sitting near zero no longer prints the same label three times.
                            return formatAxisValue(value, ticks);
                        },
                    },
                },
            }),
            ...(hasYAxis1 && {
                "y-axis-1": {
                    type: "linear",
                    position: "left",
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: combinedDatasets.find(ds => ds.type !== 'scatter' && ds.yAxisID === 'y-axis-1')?.unit || "",
                        color: theme.palette.primary.fontColor,
                        padding: 0
                    },
                    grid: {
                        color: theme.palette.mode === "dark" ? "#333" : "#e7e7e7",
                    },
                    ticks: {
                        color: theme.palette.primary.fontColor,
                        padding: 10,
                        // Zooming or panning leaves the axis ending wherever the drag stopped, and Chart.js labels
                        // that end too - 23.7 above 20, 48.3 above 45. Only the regular steps are labelled.
                        includeBounds: false,
                        callback: function (value, index, ticks) {
                            // Shared with the other charts: decimals come from the tick step, so a series
                            // sitting near zero no longer prints the same label three times.
                            return formatAxisValue(value, ticks);
                        },
                    },
                },
            }),
            // Marker axis for missing data scatter points only (at a fixed position)
            "marker-axis": {
                type: "linear",
                position: "left",
                display: false,
                min: 0,
                max: 2,
                grid: {
                    display: false,
                },
            },
        },

        animation: {
            duration: 0,
        },
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    padding: isMobile ? 8 : 10,
                    usePointStyle: true,
                    fontSize: 100,
                    color: theme.palette.primary.fontColor,
                    generateLabels: function (chart) {
                        const original = defaults.plugins.legend.labels.generateLabels;
                        const labelsOriginal = original.call(this, chart);
                        const maxLength = isMobile ? 65 : 95; // max length of the legend text

                        labelsOriginal.forEach((label, index) => {
                            const dataset = chart.data.datasets[index];
                            
                            // Keep original point style for scatter datasets (markers)
                            if (dataset && dataset.type === 'scatter') {
                                // Point style is already set correctly for markers
                            } else if (dataset && dataset.unit !== "%") {
                                label.pointStyle = "line";
                                label.lineWidth = 4;
                            }

                            if (label.hidden && typeof label.fontColor === "string") {
                                label.fontColor =
                                    theme.palette.mode === "light"
                                        ? color(label.fontColor).lighten(2).rgbString()
                                        : "#666";
                                label.fillStyle =
                                    theme.palette.mode === "light"
                                        ? color(label.fillStyle).lighten(0.5).rgbString()
                                        : "#666";
                                label.strokeStyle =
                                    theme.palette.mode === "light"
                                        ? color(label.strokeStyle).lighten(0.5).rgbString()
                                        : "#666";
                            }

                            label.hidden = false; // Ensure label is visible even if the dataset is hidden

                            if (label?.text?.length > maxLength) {
                                label.text = label.text.substring(0, maxLength) + "...";
                            }
                        });

                        // A series the country has no reading for draws nothing, so it must not
                        // appear in the legend - the dataset stays in the data for the table.
                        return labelsOriginal.filter(
                            (label) => !chart.data.datasets[label.datasetIndex]?.noData
                        );
                    },
                },
            },
            datalabels: {
                formatter: function (value, context) {
                    // Don't show labels for marker datasets
                    if (context.dataset.type === 'scatter') return '';
                    
                    // Handle both object {y: number} and primitive number formats
                    const y = typeof value === 'object' && value !== null ? value.y : value;
                    if (y === undefined || y === null || Number.isNaN(y)) return '';
                    const formattedValue = formatNumber(y);

                    //return `${y}\n${flagDescription}`;
                    return `${formattedValue}`;
                },
                color: function (context) {

                    return theme.palette.text.primary;
                },
                align: "end",
                anchor: "end",
                clamp: true,
                display: showDataLabels ? "auto" : false, //toggle visibility instead of plugin
                backgroundColor: "rgba(255, 255, 255, 0)",
                font: {
                    size: 10,
                },
                padding: 4,
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        // Special handling for marker datasets
                        if (context.dataset.type === 'scatter') {
                            const markerLabel = context.raw?.label || context.dataset.label;
                            return markerLabel;
                        }

                        const label = context.dataset.label || "";
                        const value = formatNumberWithLocale(context.parsed?.y);
                        const unit = context.dataset.unit || "";

                        // Wrap long labels - split into multiple lines if longer than 50 characters
                        const maxLength = 50;
                        const fullText = `${label} ${value} ${unit}`;

                        if (fullText.length <= maxLength) {
                            return fullText;
                        }

                        // Split the label into multiple lines
                        const lines = [];
                        let currentLine = "";
                        const words = fullText.split(" ");

                        for (const word of words) {
                            if ((currentLine + " " + word).trim().length <= maxLength) {
                                currentLine += (currentLine ? " " : "") + word;
                            } else {
                                if (currentLine) {
                                    lines.push(currentLine);
                                    currentLine = word;
                                } else {
                                    // Handle case where a single word is longer than maxLength
                                    lines.push(word);
                                }
                            }
                        }

                        if (currentLine) {
                            lines.push(currentLine);
                        }

                        return lines;
                    },
                    labelTextColor: function (context) {
                        return "#000";
                    },
                    footer: function (context) {
                        // Skip footer for marker datasets
                        if (context[0]?.dataset?.type === 'scatter') {
                            return "";
                        }
                        const flagDescription = context[0]?.raw?.flagDescription || "";
                        return flagDescription ? `${flagDescription}` : "";
                    },
                },
                backgroundColor: "#f1f1f1",
                titleColor: "#000",
                footerColor: "#333",
                footerFont: { weight: "normal" },
                borderColor: "#ccc",
                borderWidth: "1",
            },
            title: {
                display: false,
                text: title,
                font: {
                    size: 14,
                    lineHeight: 1.2,
                },
                padding: { top: 0, left: 0, right: 0, bottom: 30 },
            },
            zoom: {
                limits: {
                    x: {
                        min: 'original',
                        max: 'original',
                        minRange: 2
                    },
                    y: {
                        min: 'original',
                        max: 'original',
                        minRange: 0.1
                    },
                    y1: {
                        min: 'original',
                        max: 'original',
                        minRange: 0.1
                    }
                },
                zoom: {
                    wheel: {
                        enabled: false,
                    },
                    pinch: {
                        enabled: false
                    },
                    drag: {
                        enabled: true,
                        backgroundColor: 'rgba(225,225,225,0.3)',
                        borderColor: 'rgb(225,225,225)',
                        borderWidth: 1,
                        threshold: 100,
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'xy',
                    rangeMin: {
                        x: null,
                        y: null,
                        y1: null
                    },
                    rangeMax: {
                        x: null,
                        y: null,
                        y1: null
                    }
                }
            },
        },
    };

    return (
        <Box className="max-chart-height">
            <Bar
                options={options}
                ref={ref}
                data={{
                    labels: data.labels,
                    datasets: combinedDatasets,
                }}
            />
            <ZoomControls chartRef={ref} data={{ ...data, datasets: combinedDatasets }} />
        </Box>
    );
}
